import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import type {
  SegmentRule,
  Segment,
  SegmentInterpretation,
  VoterDataCatalogDto,
  VoterDataDistrictDto,
  VoterDataFieldDto,
  VoterDataPlaceDto,
  VoterDataPurposeDto,
  VoterDataStateDto,
} from '@signalvote/shared';
import { ListPurpose, VOTER_DATA_MAX_PLACES_PER_SEGMENT, VOTER_DATA_PRICE_PER_RECORD_CENTS, VoterDataGeography } from '@signalvote/shared';
import { ApiError } from '@signalvote/api-client';
import { useInterpretSegment, useRaceMatch, useVoterCounts, useVoterDataCatalog, useVoterHistory, useVoterPlaceLabels } from '@/hooks/queries';
import { useCampaignStore } from '@/stores';
import { VoterDataPendingError } from '@/hooks/queries/useVoterData';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { sameArea, segmentKey } from '@/lib/voterSegmentKey';
import { VoterEstimate } from './VoterCount';
import { VoterDataFieldPopoverContent, formatCount, popoverClassName } from './VoterDataFieldPopover';
import { VoterDataFieldSearch } from './VoterDataFieldSearch';
import { VoterDataAreaSearch } from './VoterDataPlacePicker';
import { VoterDataReading } from './VoterDataReading';
import { applyPurpose, nounFor } from './voterPurpose';
import { Confidence, exactList, predictList } from './voterEstimate';
import type { Estimate, ListEstimate } from './voterEstimate';
import { isNarrowed, phraseOf, valuesPhrase, withKept, withVoted } from './voterSegment';
import { readDismissedSuggestion, writeDismissedSuggestion } from './voterSavedList';
import type { PickedArea, PickedPlace, VoterDraft } from './voterSavedList';

/** A plain-English request as applied: the rules before it (for Undo) and the rules it left. */
interface AppliedReading {
  text: string;
  result: SegmentInterpretation;
  previous: SegmentRule[];
  applied: SegmentRule[];
}

/** A wait shorter than this never shows the loading line, so quick answers don't flicker. */
const LOADING_LINE_DELAY_MS = 300;

const selectClassName =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

const tokenClassName =
  'inline-flex items-baseline rounded-md border border-primary/30 bg-primary/10 font-semibold leading-snug text-primary transition-colors hover:border-primary data-[state=open]:border-primary';

/** The area token before a state is chosen: pulsing amber, so it reads as the next thing to do, not as filled in. */
const unchosenTokenClassName =
  'border-amber-500 bg-amber-50 text-amber-800 hover:border-amber-600 data-[state=open]:border-amber-600 dark:bg-amber-500/15 dark:text-amber-300 animate-amber-border-pulse motion-reduce:animate-none';

export type VoterList = ReturnType<typeof useVoterList>;

/**
 * The list a customer is building, read as a sentence: its area, purpose and rules, with the counts
 * for them. The Voter data builder lets the customer choose the purpose; a step that only ever buys
 * one kind of list passes `purpose`, which is applied, defaults and all, as soon as the catalog loads.
 *
 * `initial` restores a saved list once the catalog has loaded. A restored list wins over `purpose`.
 */
export function useVoterList({
  purpose: fixedPurpose,
  initial,
}: {
  purpose?: ListPurpose;
  initial?: (catalog: VoterDataCatalogDto, newRuleId: () => string) => VoterDraft | null;
} = {}) {
  const campaign = useCampaignStore((s) => s.selectedCampaign);
  const [state, setState] = useState('');
  const [areas, setAreas] = useState<PickedArea[]>([]);
  const [rules, setRules] = useState<SegmentRule[]>([]);
  const [purpose, setPurpose] = useState<ListPurpose | null>(null);
  // The ids of rules the current purpose added, so switching purpose can tell them from the customer's.
  const [defaultRuleIds, setDefaultRuleIds] = useState<ReadonlySet<string>>(new Set());
  const [open, setOpen] = useState<string | null>(null);
  // Set when a popover opens from the last blank, so keyboard users land inside it.
  const [focusOnOpen, setFocusOnOpen] = useState(false);
  const [reading, setReading] = useState<AppliedReading | null>(null);
  // A request whose answer came back after the list was changed by hand, so it wasn't applied.
  const [overtaken, setOvertaken] = useState<string | null>(null);
  // The rules on screen now, for a request's answer to compare with the rules it was asked about.
  const latestRules = useRef(rules);
  useEffect(() => {
    latestRules.current = rules;
  }, [rules]);
  const nextRuleId = useRef(0);
  const newRuleId = () => `r${++nextRuleId.current}`;

  const statesCatalog = useVoterDataCatalog();
  const stateCatalog = useVoterDataCatalog(state || undefined);
  const states = statesCatalog.data?.states ?? [];
  const fields = (statesCatalog.data?.categories ?? []).flatMap((c) => c.fields);
  const geographies = state ? (stateCatalog.data?.geographies ?? []) : [];
  const placeKinds = state ? (stateCatalog.data?.places ?? []) : [];
  const kinds: AreaKind[] = [
    ...geographies.map((g) => ({ kind: g.geography, label: g.label, districts: g.districts })),
    ...placeKinds.map((k) => ({ kind: k.kind, label: k.label, districts: null })),
  ];
  const kindOf = (geography: VoterDataGeography) => kinds.find((k) => k.kind === geography);
  // A restored district or place kind the state doesn't have is dropped, like any other unknown part
  // of a saved list. A place's code is checked by the server when it's counted.
  const known = areas.flatMap((area) => {
    const districts = kindOf(area.geography)?.districts;
    if (districts === undefined) return [];
    if (districts === null) return [area];
    const places = area.places.filter((p) => districts.some((d) => d.code === p.code));
    return places.length === area.places.length ? [area] : places.length ? [{ ...area, places }] : [];
  });
  if (stateCatalog.data && (known.length < areas.length || known.some((area, i) => area !== areas[i]))) setAreas(known);
  const lookedUp = useVoterPlaceLabels(
    state,
    areas
      .filter((area) => kindOf(area.geography)?.districts === null)
      .map((area) => ({ geography: area.geography, codes: area.places.filter((p) => !p.label).map((p) => p.code) }))
      .filter((area) => area.codes.length > 0),
  );
  const nameOf = (geography: VoterDataGeography, place: PickedPlace) => {
    const districts = kindOf(geography)?.districts;
    return districts ? districts.find((d) => d.code === place.code)?.label : (place.label ?? lookedUp[geography]?.[place.code]);
  };
  const labelOf = (geography: VoterDataGeography) => (place: PickedPlace) => nameOf(geography, place) ?? place.code;
  const pickedCount = areas.reduce((sum, area) => sum + area.places.length, 0);
  const full = pickedCount >= VOTER_DATA_MAX_PLACES_PER_SEGMENT;

  const purposes = statesCatalog.data?.purposes ?? [];
  const purposeDefinition = purposes.find((p) => p.purpose === purpose);
  const segment = purpose ? segmentOf(state, areas, rules, purpose) : null;
  const counts = useVoterCounts(segment);
  const stateName = states.find((s) => s.code === state)?.name ?? state;
  const areaPhrases = areas
    .filter((area) => area.places.length > 0)
    .map((area) => areaPhrase(area.geography, area.places.flatMap((p) => nameOf(area.geography, p) ?? []), area.places.length));
  const place = areaPhrases.length ? `${areaPhrases.join(', within ')}, ${stateName}` : stateName;

  const provisional = counts.isPlaceholderData && counts.isFetching;
  const history = useVoterHistory();
  const pricePerRecordCents = statesCatalog.data?.pricePerRecordCents ?? VOTER_DATA_PRICE_PER_RECORD_CENTS;
  const predicted = segment ? predictList(history, segment, fields, pricePerRecordCents) : null;
  // The answer on screen while a new one loads may be another area's, which says nothing of this one.
  const areaCounted = segment !== null && history.counts.some((h) => sameArea(h.key, segmentKey(segment)));
  const counted = segment && areaCounted && counts.data ? exactList(counts.data, pricePerRecordCents) : null;
  // With nothing to predict from, the last list's numbers say nothing of this one, so it shows as counting.
  const list = predicted ?? (provisional ? null : counted);
  const pendingSeconds = counts.failureReason instanceof VoterDataPendingError ? counts.failureReason.retryAfterSeconds : null;
  const loading = useDelayedFlag(segment !== null && counts.isFetching, LOADING_LINE_DELAY_MS);
  const total = list?.voters.value;

  // L2 combines at most maxFilters filters, the state and each area among them, so at the cap the
  // builder only edits the rules it has.
  const maxFilters = statesCatalog.data?.maxFilters;
  const capRule = `A list can have up to ${maxFilters} filters, counting the area`;
  const room = maxFilters === undefined ? Infinity : maxFilters - 1 - areas.length;
  const capReason = rules.length >= room ? `${capRule}.` : null;
  // An area is one more filter, so at the cap a list can't take another.
  const districtBlocked = areas.length === 0 && capReason ? `${capRule}. Remove a rule to choose a district.` : null;
  const [purposeNote, setPurposeNote] = useState<string | null>(null);
  // The rule to suggest removing when the list comes to no one: the one just changed, else the newest.
  const [lastChanged, setLastChanged] = useState<string | null>(null);
  const setKept = (field: VoterDataFieldDto, kept: string[]) => {
    if (capReason && !isNarrowed(field, rules)) return;
    setLastChanged(field.id);
    setRules((current) => withKept(current, field, kept, newRuleId));
  };
  const setVoted = (field: VoterDataFieldDto, voted: boolean | null) => {
    if (capReason && !isNarrowed(field, rules)) return;
    setLastChanged(field.id);
    setRules((current) => withVoted(current, field, voted, newRuleId));
  };
  const emptiedByRule = rules.find((r) => r.fieldId === lastChanged) ?? rules.at(-1);
  const emptiedByField = emptiedByRule && fields.find((f) => f.id === emptiedByRule.fieldId);
  const emptiedBy = emptiedByField ? { field: emptiedByField, phrase: phraseOf(emptiedByField, rules) } : null;
  const removeRule = (fieldId: string) => {
    setRules((current) => current.filter((r) => r.fieldId !== fieldId));
    setOpen(null);
  };

  // A plain-English request applies the moment it's read, with no confirm step. Rules it didn't
  // touch keep their ids; the server's ids for new rules are replaced with the builder's own, so
  // they can't collide with rules added by hand later. An answer that arrives after the list was
  // changed by hand is dropped: it was read against a list that's no longer there.
  // `onApplied` clears the blank, so text that wasn't applied stays there to edit or retry.
  const { mutate: interpret, isPending: interpreting, error: interpretError } = useInterpretSegment();
  // Describe is offered only where requests can be read: the catalog says whether this site can,
  // and a 503 says the reader is down for now.
  const interpreterDown = interpretError instanceof ApiError && interpretError.status === 503;
  const canDescribe = statesCatalog.data?.interpreterAvailable === true && !interpreterDown;
  const applyRequest = (text: string, onApplied: () => void) => {
    if (!purpose) return;
    const previous = rules;
    setOvertaken(null);
    interpret(
      { text, segment: segment ?? { state, areas: [], rules, purpose, onePerHousehold: false } },
      {
        onSuccess: (result) => {
          if (latestRules.current !== previous) {
            setOvertaken(text);
            return;
          }
          const known = new Set(previous.map((r) => r.id));
          const applied = result.rules.map((r) => (known.has(r.id) ? r : { ...r, id: newRuleId() }));
          setRules(applied);
          setReading({ text, result, previous, applied });
          onApplied();
        },
      },
    );
  };
  const undoReading = () => {
    if (reading) setRules(reading.previous);
    setReading(null);
  };

  const choosePurpose = (next: VoterDataPurposeDto) => {
    const applied = applyPurpose(rules, defaultRuleIds, purposeDefinition, next, newRuleId, room);
    const leftOut = applied.leftOut.flatMap((d) => {
      const field = fields.find((f) => f.id === d.fieldId);
      return field ? [`“${valuesPhrase(field, d.values)}”`] : [];
    });
    setPurposeNote(
      leftOut.length ? `${capRule}, so the ${next.label} ${leftOut.length === 1 ? 'default' : 'defaults'} ${leftOut.join(' and ')} ${leftOut.length === 1 ? 'wasn’t' : 'weren’t'} added.` : null,
    );
    setRules(applied.rules);
    setDefaultRuleIds(applied.defaultIds);
    setPurpose(next.purpose);
  };

  const [ready, setReady] = useState(false);
  if (!ready && statesCatalog.data) {
    setReady(true);
    const draft = initial?.(statesCatalog.data, newRuleId) ?? null;
    const fixed = fixedPurpose === undefined ? undefined : purposes.find((p) => p.purpose === fixedPurpose);
    // A list with no state yet starts in the state the campaign said its race is in.
    const campaignState = campaign?.state && statesCatalog.data.states.some((s) => s.code === campaign.state) ? campaign.state : '';
    if (draft) {
      setState(draft.state || campaignState);
      setAreas(draft.areas);
      setRules(draft.rules);
      setPurpose(draft.purpose);
      setDefaultRuleIds(draft.defaultRuleIds);
    } else {
      setState(campaignState);
      if (fixed) choosePurpose(fixed);
    }
  }

  // The district the campaign's race is elected from, offered while the list has no area. The race is what the
  // person said they're running for, else the campaign's name.
  const race = campaign?.office?.trim() || campaign?.name?.trim() || '';
  const match = useRaceMatch(state, race, areas.length === 0);
  const matched = match.data?.confident ? match.data.match : null;
  const suggestion = matched && kindOf(matched.geography) ? matched : null;
  const suggestionKey = suggestion ? `${state}:${suggestion.geography}:${suggestion.code}` : null;
  const [dismissed, setDismissed] = useState(() => readDismissedSuggestion(campaign?.id ?? null));
  const suggesting = suggestion !== null && areas.length === 0 && dismissed !== suggestionKey;
  // A list ordered past the card would be the area it reads now, often the whole state, so ordering waits on an answer.
  const unanswered = suggesting ? 'Answer the question above first.' : null;
  const dismissSuggestion = () => {
    if (!suggestionKey) return;
    writeDismissedSuggestion(campaign?.id ?? null, suggestionKey);
    setDismissed(suggestionKey);
  };

  const chooseState = (code: string) => {
    setState(code);
    setAreas([]);
  };
  const wholeState = () => setAreas([]);
  const removeArea = (index: number) => setAreas((current) => current.filter((_, i) => i !== index));
  // An area left with no places is gone, so the list reads as the state again.
  const editPlaces = (index: number, edit: (places: PickedPlace[]) => PickedPlace[]) =>
    setAreas((current) =>
      current.map((area, i) => (i === index ? { ...area, places: edit(area.places) } : area)).filter((area) => area.places.length > 0),
    );
  // A district's name comes from the catalog, so only a place keeps its name.
  const placeOf = ({ geography, code, label }: VoterDataPlaceDto): PickedPlace => (kindOf(geography)?.districts ? { code } : { code, label });
  // A result of the area's kind is added or, picked again, removed; one of another kind replaces the area,
  // since a list has one area.
  const pickPlace = (place: VoterDataPlaceDto) => {
    const index = areas.findIndex((area) => area.geography === place.geography);
    if (index < 0) {
      setAreas([{ geography: place.geography, places: [placeOf(place)] }]);
      return;
    }
    const picked = areas[index].places.some((p) => p.code === place.code);
    if (!picked && full) return;
    editPlaces(index, (places) => (picked ? places.filter((p) => p.code !== place.code) : [...places, placeOf(place)]));
  };
  const kindLabelOf = (geography: VoterDataGeography) => kindOf(geography)?.label ?? geography;
  const removePlace = (index: number, code: string) => editPlaces(index, (places) => places.filter((p) => p.code !== code));
  // The state's catalog held up by L2's rate limit is a wait, not an error: it's asked again when the server says it may be.
  const placesWait =
    state && !stateCatalog.data && !stateCatalog.isError && stateCatalog.failureReason instanceof ApiError && stateCatalog.failureReason.status === 429
      ? { seconds: stateCatalog.failureReason.retryAfterSeconds }
      : null;

  // The field whose token opened the popover stays in the sentence while it's open, even once
  // it's back to Everyone, so the popover keeps its anchor.
  const tokenFields = rules.map((r) => fields.find((f) => f.id === r.fieldId)).filter((f): f is VoterDataFieldDto => f !== undefined);
  const openTokenField = open?.startsWith('token:') ? fields.find((f) => `token:${f.id}` === open) : undefined;
  if (openTokenField && !tokenFields.includes(openTokenField)) tokenFields.push(openTokenField);

  const sentenceText = `Voters in ${place}${tokenFields
    .map((field, i) => joinerOf(i, tokenFields.length) + phraseOf(field, rules))
    .join('')}.`;

  return {
    ready,
    catalog: statesCatalog,
    stateCatalog,
    states,
    fields,
    kinds,
    placesWait,
    purposes,
    purpose,
    purposeDefinition,
    purposeNote,
    choosePurpose,
    state,
    chooseState,
    areas,
    wholeState,
    pickPlace,
    removeArea,
    labelOf,
    kindLabelOf,
    removePlace,
    suggestion,
    suggesting,
    unanswered,
    dismissSuggestion,
    full,
    stateName,
    place,
    districtBlocked,
    rules,
    defaultRuleIds,
    setKept,
    setVoted,
    removeRule,
    capReason,
    emptiedBy,
    segment,
    counts,
    list,
    total,
    provisional,
    pendingSeconds,
    loading,
    tokenFields,
    sentenceText,
    open,
    setOpen,
    focusOnOpen,
    setFocusOnOpen,
    canDescribe,
    applyRequest,
    interpreting,
    interpretError,
    overtaken,
    reading,
    setReading,
    undoReading,
  };
}

/**
 * The list read as a sentence ("Voters in House District 28 who are not Republican and are women"),
 * with the count under it. Each highlighted phrase opens its field in a popover that grows from it,
 * and the sentence's last blank finds fields and values to add, or takes a plain-English request
 * that applies at once, says how it was read and can be undone. Shows nothing until the list has a purpose.
 *
 * `countsAs` names what the count counts, in place of "voters in {place}": a list that keeps only
 * voters with a cell phone says so, or its count reads as every voter in the area.
 */
export function VoterListSentence({
  list: v,
  headingRef,
  countsAs,
}: {
  list: VoterList;
  headingRef?: (heading: HTMLHeadingElement | null) => void;
  countsAs?: (count: number) => string;
}) {
  if (!v.purposeDefinition) return null;

  const openHandler = (key: string) => (isOpen: boolean) => {
    v.setFocusOnOpen(false);
    v.setOpen(isOpen ? key : null);
  };
  // A field chosen from the last blank joins the sentence as a token with its popover open.
  const openFromSearch = (field: VoterDataFieldDto) => {
    v.setFocusOnOpen(true);
    v.setOpen(`token:${field.id}`);
  };
  // The search lists bare counts with nothing dimmed, so it shows only this list's own answer, never the one before.
  const currentCounts = v.counts.isPlaceholderData ? undefined : v.counts.data;

  return (
    <>
      {v.suggesting && v.suggestion && (
        <SuggestionCard place={v.suggestion} onYes={() => v.pickPlace(v.suggestion!)} onNo={v.dismissSuggestion} />
      )}
      <h2
        ref={headingRef}
        className="text-[clamp(1.75rem,1.15rem+1.7vw,2.5rem)] font-medium leading-[1.62] tracking-[-0.024em] text-foreground/75 [text-wrap:pretty]"
      >
        Voters in{' '}
        <Popover open={v.open === 'area'} onOpenChange={openHandler('area')}>
          <PopoverTrigger asChild>
            <button type="button" aria-label="Change area" className={cn(tokenClassName, 'px-2.5', !v.state && unchosenTokenClassName)}>
              {v.place || 'choose a state'}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            // Picked places and search results can outgrow the screen, so the area scrolls inside it.
            className={cn(popoverClassName, 'max-h-[var(--radix-popover-content-available-height)] overflow-y-auto')}
          >
            <AreaPicker
              states={v.states}
              state={v.state}
              areas={v.areas}
              labelOf={v.labelOf}
              kindLabelOf={v.kindLabelOf}
              full={v.full}
              statesFailed={v.catalog.isError}
              districtsFailed={!!v.state && v.stateCatalog.isError && !v.catalog.isError}
              placesWait={v.placesWait}
              stateName={v.stateName}
              districtBlocked={v.districtBlocked}
              suggestion={v.suggestion}
              onState={v.chooseState}
              onWholeState={v.wholeState}
              onPick={v.pickPlace}
              onRemoveArea={v.removeArea}
              onRemovePlace={v.removePlace}
              onRetry={() => void v.stateCatalog.refetch()}
              onDone={() => v.setOpen(null)}
            />
          </PopoverContent>
        </Popover>
        {v.tokenFields.map((field, i) => (
          <Fragment key={field.id}>
            {joinerOf(i, v.tokenFields.length)}
            <Popover open={v.open === `token:${field.id}`} onOpenChange={openHandler(`token:${field.id}`)}>
              <motion.span layout="position" className={cn(tokenClassName, 'pl-2.5', !isNarrowed(field, v.rules) && 'border-dashed')}>
                <PopoverTrigger asChild>
                  <button type="button" aria-label={`Edit ${field.label}`} className="rounded-sm">
                    {phraseOf(field, v.rules)}
                  </button>
                </PopoverTrigger>
                <button
                  type="button"
                  aria-label={`Remove ${field.label} filter`}
                  onClick={() => v.removeRule(field.id)}
                  className="ml-0.5 mr-1 inline-flex size-[1.3em] items-center justify-center self-center rounded opacity-50 hover:bg-primary/15 hover:opacity-100"
                >
                  <X className="size-[0.6em]" strokeWidth={3} aria-hidden="true" />
                </button>
              </motion.span>
              <VoterDataFieldPopoverContent
                open={v.open === `token:${field.id}`}
                field={field}
                segment={v.segment}
                rules={v.rules}
                counts={v.counts.data}
                total={v.total}
                provisional={v.provisional}
                focusOnOpen={v.focusOnOpen}
                capReason={v.capReason}
                onKeep={(kept) => v.setKept(field, kept)}
                onVoted={(voted) => v.setVoted(field, voted)}
                onDone={() => v.setOpen(null)}
              />
            </Popover>
          </Fragment>
        ))}
        {v.state && (
          <VoterDataFieldSearch
            fields={v.fields}
            rules={v.rules}
            counts={currentCounts}
            placeholder={v.tokenFields.length ? 'and who else…' : 'who…'}
            capReason={v.capReason}
            onChooseField={openFromSearch}
            onChooseValue={(field, option) => v.setKept(field, [option.label])}
            onDescribe={v.canDescribe ? v.applyRequest : undefined}
          />
        )}
        .
      </h2>

      <CountLine
        segment={v.segment}
        state={v.state}
        unpicked={v.kinds.find((k) => v.areas.some((a) => a.geography === k.kind && a.places.length === 0))}
        counts={v.counts}
        list={v.list}
        provisional={v.provisional}
        pendingSeconds={v.pendingSeconds}
        place={v.place}
        countsAs={countsAs}
        emptiedBy={v.emptiedBy}
        onRemove={v.removeRule}
      />

      {v.interpreting ? (
        <p role="status" className="mt-5 text-sm text-muted-foreground">
          Reading your request…
        </p>
      ) : v.interpretError ? (
        <p role="alert" className="mt-5 text-sm text-destructive">
          {v.interpretError instanceof ApiError && v.interpretError.message
            ? v.interpretError.message
            : 'Couldn’t read that request. Please try again, or search for a field.'}
        </p>
      ) : v.overtaken ? (
        <p role="status" className="mt-5 text-sm text-muted-foreground">
          Your list changed while I was reading “{v.overtaken}”, so I left it as it is. Try the request again.
        </p>
      ) : (
        v.reading && (
          <VoterDataReading
            text={v.reading.text}
            result={v.reading.result}
            fields={v.fields}
            canUndo={v.rules === v.reading.applied}
            onUndo={v.undoReading}
            onDismiss={() => v.setReading(null)}
            onOpenField={openFromSearch}
          />
        )
      )}
    </>
  );
}

/** How the sentence joins its phrases: "who a", "who a and b", "who a, b and c". */
export function joinerOf(index: number, count: number): string {
  if (index === 0) return ' who ';
  return index === count - 1 ? ' and ' : ', ';
}

/** Picked places in a few words: "A", "A and B", or "A, B and 3 more". Places whose names aren't known yet are counted. */
function placesPhrase(names: string[], count: number, noun = 'place'): string {
  const shown = names.slice(0, 2);
  const more = count - shown.length;
  if (more <= 0) return shown.join(' and ');
  if (shown.length === 0) return `${count} ${nounFor(count, noun)}`;
  return `${shown.join(', ')} and ${more} more`;
}

const NUMBERED = /^(.+) (\d+)$/;

/**
 * An area in a few words: "House Districts 27 and 28" for numbered districts of one kind, "Jefferson
 * County" or "Jefferson and Shelby counties" for counties, else its places' names.
 */
function areaPhrase(geography: VoterDataGeography, names: string[], count: number): string {
  if (geography === VoterDataGeography.County) {
    return `${placesPhrase(names, count, 'county')}${names.length === 0 ? '' : count === 1 ? ' County' : ' counties'}`;
  }
  const numbered = names.map((name) => NUMBERED.exec(name));
  const prefix = numbered[0]?.[1];
  if (prefix && count > 1 && names.length === count && numbered.every((m) => m?.[1] === prefix)) {
    return `${prefix}s ${placesPhrase(numbered.map((m) => m![2]), count)}`;
  }
  return placesPhrase(names, count);
}

/** A kind of area the state offers: one of its district kinds, with the catalog's districts, or a place kind, searched. */
interface AreaKind {
  kind: VoterDataGeography;
  label: string;
  districts: VoterDataDistrictDto[] | null;
}

function segmentOf(state: string, areas: PickedArea[], rules: SegmentRule[], purpose: ListPurpose): Segment | null {
  if (!state) return null;
  if (areas.some((area) => area.places.length === 0)) return null;
  // Lists are sold per voter only; the field stays on the wire for orders placed before.
  return { state, areas: areas.map((area) => ({ geography: area.geography, codes: area.places.map((p) => p.code) })), rules, purpose, onePerHousehold: false };
}

// ============================================
// The count under the sentence
// ============================================

function CountLine({
  segment,
  state,
  unpicked,
  counts,
  list,
  provisional,
  pendingSeconds,
  place,
  countsAs,
  emptiedBy,
  onRemove,
}: {
  segment: Segment | null;
  state: string;
  /** The kind of the first area with nothing picked yet. */
  unpicked: AreaKind | undefined;
  counts: ReturnType<typeof useVoterCounts>;
  list: ListEstimate | null;
  provisional: boolean;
  pendingSeconds: number | null;
  place: string;
  countsAs?: (count: number) => string;
  emptiedBy: { field: VoterDataFieldDto; phrase: string } | null;
  onRemove: (fieldId: string) => void;
}) {
  const line = 'mt-5 text-[1.0625rem] leading-relaxed text-muted-foreground';
  if (!segment) {
    return (
      <p className={line}>
        {!state
          ? 'Choose a state to see how many voters it has.'
          : `Choose at least one ${unpicked?.label.toLowerCase() ?? 'district'} to see how many voters are in it.`}
      </p>
    );
  }

  if (counts.error) {
    return (
      <p role="alert" className="mt-5 text-sm text-destructive">
        {counts.error instanceof ApiError && counts.error.status === 400
          ? counts.error.message
          : 'Couldn’t count voters right now. Please try again shortly.'}
      </p>
    );
  }
  const exact = (estimate: Estimate) => estimate.confidence === Confidence.Exact;
  const toCome = !list || !exact(list.voters) ? 'numbers' : !exact(list.households) ? 'households' : null;
  const pendingNote = pendingSeconds !== null && toCome && (
    <p role="status" className="mt-1 text-sm text-muted-foreground">
      Exact {toCome} in about {pendingSeconds} s.
    </p>
  );
  if (!list) {
    return (
      <>
        <p className={line}>Counting voters…</p>
        {pendingNote}
      </>
    );
  }

  return (
    <>
      <p aria-busy={provisional} aria-live="polite" className={line}>
        That’s{' '}
        <b className="font-bold tabular-nums text-foreground">
          <VoterEstimate estimate={list.voters} />
        </b>{' '}
        {countsAs ? countsAs(list.voters.value) : `${nounFor(list.voters.value, 'voter')} in ${place}`},{' '}
        <span className="tabular-nums">
          <VoterEstimate estimate={list.households} />
        </span>{' '}
        {nounFor(list.households.value, 'household')}.
      </p>
      {list.voters.value === 0 && emptiedBy && (
        <p className="mt-1 text-sm text-muted-foreground">
          No voters match this list.{' '}
          <button type="button" onClick={() => onRemove(emptiedBy.field.id)} className="font-semibold text-primary underline-offset-2 hover:underline">
            Remove “{emptiedBy.phrase}”
          </button>{' '}
          to widen it.
        </p>
      )}
      {pendingNote}
    </>
  );
}

// ============================================
// Area
// ============================================

function AreaPicker(props: {
  states: VoterDataStateDto[];
  state: string;
  areas: PickedArea[];
  labelOf: (geography: VoterDataGeography) => (place: PickedPlace) => string;
  kindLabelOf: (geography: VoterDataGeography) => string;
  full: boolean;
  statesFailed: boolean;
  districtsFailed: boolean;
  /** Set while the state's districts and places wait on L2's rate limit. */
  placesWait: { seconds: number | null } | null;
  stateName: string;
  /** Why no district can be chosen, at L2's filter cap. */
  districtBlocked: string | null;
  suggestion: VoterDataPlaceDto | null;
  onState: (code: string) => void;
  onWholeState: () => void;
  onPick: (place: VoterDataPlaceDto) => void;
  onRemoveArea: (index: number) => void;
  onRemovePlace: (index: number, code: string) => void;
  onRetry: () => void;
  onDone: () => void;
}) {
  const stateId = useId();
  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">Area</h3>
      {props.statesFailed && (
        <p role="alert" className="text-sm text-destructive">
          Couldn’t load the list of states. Please refresh to try again.
        </p>
      )}
      {props.districtsFailed && (
        <p role="alert" className="text-sm text-destructive">
          Couldn’t load the districts in {props.stateName}. You can still count the whole state.{' '}
          <button type="button" onClick={props.onRetry} className="font-semibold underline-offset-2 hover:underline">
            Try again
          </button>
        </p>
      )}
      {props.placesWait && (
        <p role="status" className="text-sm text-muted-foreground">
          The voter file is busy. Trying again for {props.stateName}’s districts and places{' '}
          {props.placesWait.seconds ? `in about ${props.placesWait.seconds} s` : 'in a few minutes'}.
        </p>
      )}
      <div className="space-y-1.5">
        <Label htmlFor={stateId}>State</Label>
        <select
          id={stateId}
          value={props.state}
          onChange={(e) => props.onState(e.target.value)}
          disabled={props.states.length === 0}
          className={selectClassName}
        >
          <option value="">Choose a state</option>
          {[...props.states]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
        </select>
      </div>
      {props.state && (
        <VoterDataAreaSearch
          state={props.state}
          areas={props.areas}
          labelOf={props.labelOf}
          kindLabelOf={props.kindLabelOf}
          full={props.full}
          blocked={props.districtBlocked}
          suggestion={props.suggestion}
          onWholeState={props.onWholeState}
          onPick={props.onPick}
          onRemovePlace={props.onRemovePlace}
          onRemoveArea={props.onRemoveArea}
        />
      )}
      <div className="flex justify-end border-t pt-3">
        <Button size="sm" onClick={props.onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

/** "Is this your district?": the district the campaign's race matched, offered before any area is chosen. */
function SuggestionCard({ place, onYes, onNo }: { place: VoterDataPlaceDto; onYes: () => void; onNo: () => void }) {
  return (
    <section aria-label="Suggested district" className="mb-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
      <p className="text-sm font-semibold text-foreground">Is this your district?</p>
      <p className="mt-1 text-[1.0625rem] text-foreground">
        {place.label} <span className="text-muted-foreground">· {formatCount(place.voters)} {nounFor(place.voters, 'voter')}</span>
      </p>
      <div className="mt-3 flex gap-2">
        <Button type="button" size="sm" onClick={onYes}>
          Yes, use this
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onNo}>
          Not this one
        </Button>
      </div>
    </section>
  );
}
